import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/errorHandler";
import { setUserContext } from "@/lib/sentry";
import { profileUpdateSchema, validateData } from "@/lib/validations";
import { readAttribution, clearAttribution } from "@/lib/attribution";
interface Profile {
  id: string;
  full_name: string | null;
  subscription_status: "free" | "social_ran" | "premium" | "muy_pro";
  /** Cuenta de cortesía: cuenta como Club en TODOS los gates aunque
   *  subscription_status siga en 'free'. Usar via isEffectivelyPremium(). */
  is_complimentary: boolean;
  mp_subscription_id: string | null;
  mp_subscription_plan: string | null;
  runner_level: "beginner" | "intermediate" | "pro" | null;
  avatar_url: string | null;
  // Onboarding fields
  max_distance: string | null;
  goals: string[];
  sports: string[];
  how_found_us: string | null;
  neighborhood: string | null;
  onboarding_completed_at: string | null;
  // New onboarding fields
  birth_date: string | null;
  phone: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  medical_notes: string | null;
  pace_range: string | null;
  accepted_responsibility: boolean;
  dni: string | null;
  blood_type: string | null;
  health_provider: string | null;
  accepted_responsibility_at: string | null;
  accepted_terms_version: string | null;
  current_focus: string | null;
  objective: string | null;
  has_injury: boolean | null;
  injury_notes: string | null;
  // Strava
  strava_connected: boolean;
  strava_athlete_id: string | null;
  strava_last_sync_at: string | null;
  strava_auto_publish: boolean;
  // Feed customization (premium)
  feed_card_color: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Identify in Sentry as soon as we know who's logged in.
        setUserContext({ id: session.user.id, email: session.user.email });
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes - NO async callbacks to prevent deadlock
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setUserContext({ id: session.user.id, email: session.user.email });
          // Defer Supabase call with setTimeout to prevent deadlock
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setUserContext(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        // Persistir atribución (first-touch) si la tenemos en localStorage y
        // el profile aún no la tiene seteada. El `.is("signup_source", null)`
        // es la guarda de idempotencia: si ya estaba seteado no se sobreescribe.
        const attr = readAttribution();
        if (attr) {
          try {
            const { error: attrError } = await supabase
              .from("profiles")
              .update({
                // FASE4: columns recién agregadas, types aún no regenerados
                signup_source: attr.utm_source ?? null,
                signup_medium: attr.utm_medium ?? null,
                signup_campaign: attr.utm_campaign ?? null,
                signup_referrer: attr.referrer ?? null,
                signup_landing_path: attr.landing_path ?? null,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any)
              .eq("id", userId)
              .is("signup_source", null);
            if (!attrError) {
              clearAttribution();
            }
          } catch (e) {
            // No-op: atribución es best-effort, no bloqueamos el login si falla.
            logError("attribution-persist", e);
          }
        }

        // Enrich Sentry user with the display name once profile is loaded.
        setUserContext({
          id: data.id,
          email: user?.email,
          full_name: data.full_name,
        });
        setProfile({
          id: data.id,
          full_name: data.full_name,
          subscription_status: data.subscription_status as "free" | "social_ran" | "premium" | "muy_pro",
          is_complimentary: ((data as Record<string, unknown>).is_complimentary as boolean) ?? false,
          mp_subscription_id: (data as Record<string, unknown>).mp_subscription_id as string | null,
          mp_subscription_plan: (data as Record<string, unknown>).mp_subscription_plan as string | null,
          runner_level: data.runner_level as "beginner" | "intermediate" | "pro" | null,
          avatar_url: data.avatar_url,
          max_distance: data.max_distance,
          goals: data.goals ?? [],
          sports: data.sports ?? [],
          how_found_us: data.how_found_us,
          neighborhood: data.neighborhood,
          onboarding_completed_at: data.onboarding_completed_at,
          birth_date: (data as Record<string, unknown>).birth_date as string | null,
          phone: (data as Record<string, unknown>).phone as string | null,
          emergency_contact_name: (data as Record<string, unknown>).emergency_contact_name as string | null,
          emergency_contact_phone: (data as Record<string, unknown>).emergency_contact_phone as string | null,
          medical_notes: (data as Record<string, unknown>).medical_notes as string | null,
          pace_range: (data as Record<string, unknown>).pace_range as string | null,
          accepted_responsibility: ((data as Record<string, unknown>).accepted_responsibility as boolean) ?? false,
          dni: (data as Record<string, unknown>).dni as string | null,
          blood_type: (data as Record<string, unknown>).blood_type as string | null,
          health_provider: (data as Record<string, unknown>).health_provider as string | null,
          accepted_responsibility_at: (data as Record<string, unknown>).accepted_responsibility_at as string | null,
          accepted_terms_version: (data as Record<string, unknown>).accepted_terms_version as string | null,
          current_focus: (data as Record<string, unknown>).current_focus as string | null,
          objective: (data as Record<string, unknown>).objective as string | null,
          has_injury: (data as Record<string, unknown>).has_injury as boolean | null,
          injury_notes: (data as Record<string, unknown>).injury_notes as string | null,
          strava_connected: ((data as Record<string, unknown>).strava_connected as boolean) ?? false,
          strava_athlete_id: (data as Record<string, unknown>).strava_athlete_id as string | null,
          strava_last_sync_at: (data as Record<string, unknown>).strava_last_sync_at as string | null,
          strava_auto_publish: ((data as Record<string, unknown>).strava_auto_publish as boolean) ?? false,
          feed_card_color: (data as Record<string, unknown>).feed_card_color as string | null,
        });
      }
    } catch (error) {
      logError("profile-fetch", error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const currentUserId = user?.id;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    if (currentUserId && typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(`ranerzzz:onboarding-draft:v1:${currentUserId}`);
        window.localStorage.removeItem("ranerzzz:onboarding-draft:v1");
      } catch {
        // ignore quota / disabled storage
      }
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error("No user");

    // Validate profile updates
    const validation = validateData(profileUpdateSchema.partial(), updates);
    if (validation.success === false) {
      throw new Error(validation.error);
    }

    const validatedData = validation.data;
    const { error } = await supabase
      .from("profiles")
      .update(validatedData as Record<string, unknown>)
      .eq("id", user.id);

    if (error) throw error;

    setProfile((prev) => (prev ? { ...prev, ...updates } : null));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
